import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import styled from 'styled-components/macro';

import { liberalPrefixOptions } from './Prefix/utils';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import Button from '../../components/Button';
import DropDownSelect from '../DropDownSelect';
import InputGroup from '../InputGroup';
import PrefixPopup from './Prefix/PrefixPopup';
import ReactGoogleAutocomplete from '../InputFields/GoogleMapsAddressField';
import TwoGroup from '../../components/TwoGroup';
import { styledScrollbar } from '../../components/Shared/mixins';

import GetUpdatesCheckbox from '../Widget/GetUpdatesCheckbox';
import LegalInfo from '../Widget/LegalInfo';

import { requiredFormElements } from './representatives';
import { isPortrait } from '../../utils/size';
import { getCachedFullName } from '../../utils/formCaching/helpers';
import { cacheFormData } from '../../utils/formCaching/actions';
import { findCachedValueByActionIdentifier } from '../../utils/formCaching/helpers';

import { loadLocalRepSecondaryAddressCheck } from '../../containers/LocalRep/actions';
import { OnSubmitValidator } from '../../containers/DefaultStyles/FormDetails';
import submissionErrorHandler from '../../utils/submissionErrorHandler';
import { ThemeConsumer } from '../../utils/theming';

import { getActiveTopic } from './DetailsFormMultiRepStep1';

import { copyFor, capitalization } from '../../copy/copy';

import { validateStep2 } from './validations';

const DetailsFormContainer = styled.form`
    flex: 1;
    padding: 0;
    display: flex;
    flex-direction: column;
`;

const Row = styled.div`
    margin: 0 -0.5em;
    transition: opacity 0.6s;
    overflow-x: hidden;
    max-height: 81%;
    overflow-y: auto;

    ${({ theme }) => !isPortrait() && styledScrollbar(theme.colors.primary)};

    .message-response {
        bottom: 80px;
    }

    ${() => !isPortrait() && styledScrollbar};

    .label-response {
        @media (min-width: 30em) {
            position: absolute;
            width: 100%;
            bottom: 142px;
            transition: bottom 0.6s;
        }
    }

    &.focused {
        &.row-wrapper-3 {
            opacity: 1;

            .label-response {
                bottom: calc(100% - 16px);
            }

            textarea {
                height: 200px;
            }
        }
    }
`;

const DynamicEmailRow = styled.div`
    width: ${() => (isPortrait() ? 100 : 50)}%;
    padding: 0 0.5em;
    float: left;
    position: relative;
`;

const HalfRow = styled.div`
    width: 50%;
    padding: 0 0.5em;
    float: left;
    position: relative;
    &:last-child {
        width: 100%;
    }
`;

const QuarterRow = styled.div`
    width: 25%;
    padding: 0 0.5em;
    float: left;
    position: relative;
`;

const ThreeQuarterRow = styled.div`
    width: 75%;
    padding: 0 0.5em;
    float: left;
    position: relative;
`;

const DynamicNameRow = styled(ThreeQuarterRow)`
    ${props => !props.includesPrefix && `width: 100%`};
`;

const FullRow = styled(ThreeQuarterRow)`
    width: 100%;
`;

const ButtonGroup = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    position: absolute;
    right: 0;
    bottom: 10px;
    width: 70%;

    @media (max-width: 30em) {
        width: 100%;
    }

    > div {
        position: relative;
    }

    button {
        &.submitButton {
            ${props => (props.isSubmitting ? `text-indent: -99999px;` : '')};
            margin-left: 0.5em;

            @media (max-width: 30em) {
                width: calc(50% - 0.5em);
            }
        }

        &.middleButton {
            @media (max-width: 30em) {
                width: calc(50% - 0.5em);
                margin-right: 0.5em;
            }
        }

        &:focus {
            outline: none;
        }

        + div {
            position: absolute;
            display: none;
            top: 7px;
            right: 24%;
            margin-right: -16px;
            width: 32px !important;
            height: 32px !important;
            ${props => (props.isSubmitting ? `display: block` : '')};
        }
    }
`;

const LegalInfoWrapper = styled.div`
    position: absolute;
    left: 0;
    bottom: 10px;
    width: 30%;
    padding-right: 10px;

    @media (max-width: 30em) {
        width: 100%;
        bottom: 70px;
        padding-right: 0;
    }
`;

const FormGroupWrapper = styled.div`
    @media (max-width: 30em) {
        flex: 1;
        display: flex;
        flex-direction: row;
        margin: 0 -0.5em;

        > div {
            flex: 1;
            display: flex;
            flex-direction: column;
            margin: 0 0.5em;
            padding-top: 0px;
        }
    }
`;

class DetailsFormMultiRepStep2 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            valid: false,
            errors: submissionErrorHandler(props.currentFormValues, validateStep2, {
                isRecordVideoAction: this.props.isRecordVideoAction
            })
        };
        this.clearSubmitErrors = this.clearSubmitErrors.bind(this);
    }

    componentDidMount() {
        this.setState({ errors: [] });
    }

    setValidState(bool) {
        this.setState({ valid: bool });
    }

    setSubmitErrors(errors) {
        this.setState({ errors });
    }

    checkForBlurError(name) {
        const errors = submissionErrorHandler(
            this.props.currentFormValues,
            validateStep2,
            { isRecordVideoAction: this.props.isRecordVideoAction }
        );
        if (errors.includes(name) && !this.state.errors.includes(name))
            this.setState({ errors: [...this.state.errors, name] });
    }

    clearSubmitErrors(fieldName) {
        if (fieldName && this.state.errors.includes(fieldName)) {
            this.setState({ errors: this.state.errors.filter(e => e !== fieldName) });
        }
    }

    render() {
        return (
            <ThemeConsumer>
                {theme => (
                    <DetailsFormContainer autoComplete="on">
                        <Row theme={theme}>
                            {this.props.isRecordVideoAction && (
                                <FullRow textAreaFocused={this.state.textAreaFocused}>
                                    {this.props.requiredFormElements
                                        .filter(function(a) {
                                            return a.name === 'Topic';
                                        })
                                        .map(a => (
                                            <Field
                                                key={a.index}
                                                name="topic"
                                                component={
                                                    a.options.length > 0
                                                        ? DropDownSelect
                                                        : InputGroup
                                                }
                                                options={a.options}
                                                defaultOption={
                                                    this.props.initialValues.topic ||
                                                    a.defaultOption
                                                }
                                                label="Subject"
                                                type="text"
                                                onChange={e => {
                                                    this.props.cacheFormData(
                                                        { topic: e.target.value },
                                                        this.props.actionIdentifier
                                                    );
                                                }}
                                            />
                                        ))}
                                </FullRow>
                            )}
                            {this.props.isRecordVideoAction &&
                                this.props.subject &&
                                !this.props.includesTopic && (
                                    <FullRow>
                                        <Field
                                            name="subject"
                                            component={InputGroup}
                                            onBlur={() =>
                                                this.checkForBlurError('subject')
                                            }
                                            submissionError={this.state.errors.includes(
                                                'subject'
                                            )}
                                            label="Subject"
                                            type="text"
                                            value={this.props.subject}
                                        />
                                    </FullRow>
                                )}
                            {this.props.requiredFormElements
                                .filter(a => a.name === 'NamePrefix')
                                .map(a => (
                                    <QuarterRow key={a.index}>
                                        <Field
                                            className={'fs-block'}
                                            name="prefix"
                                            onBlur={() =>
                                                this.checkForBlurError('prefix')
                                            }
                                            submissionError={this.state.errors.includes(
                                                'prefix'
                                            )}
                                            clearSubmitErrors={this.clearSubmitErrors}
                                            component={
                                                a.options.length > 0
                                                    ? DropDownSelect
                                                    : InputGroup
                                            }
                                            options={liberalPrefixOptions(a.options)}
                                            defaultOption={a.defaultOption}
                                            type="text"
                                            label={a.placeholder}
                                            placeholder={a.placeholder}
                                            onChange={e => {
                                                if (e.target && e.target.value) {
                                                    if (
                                                        a.options.indexOf(
                                                            e.target.value
                                                        ) === -1
                                                    ) {
                                                        this.props.setPrefixModalVisibility(
                                                            true
                                                        );
                                                    }
                                                }
                                            }}
                                        />
                                    </QuarterRow>
                                ))}
                            <DynamicNameRow includesPrefix={this.props.includesPrefix}>
                                <Field
                                    className={'fs-block'}
                                    name="fullName"
                                    onBlur={() => this.checkForBlurError('fullName')}
                                    submissionError={this.state.errors.includes(
                                        'fullName'
                                    )}
                                    clearSubmitErrors={this.clearSubmitErrors}
                                    component={InputGroup}
                                    type="text"
                                    label={copyFor(
                                        'formFields',
                                        'fullName',
                                        'email',
                                        capitalization.eachWord
                                    )}
                                    placeholder={copyFor(
                                        'placeholders',
                                        'fullName',
                                        'email'
                                    )}
                                />
                            </DynamicNameRow>
                            <DynamicEmailRow>
                                <Field
                                    className={'fs-block'}
                                    name="email"
                                    onBlur={() => this.checkForBlurError('email')}
                                    submissionError={this.state.errors.includes('email')}
                                    clearSubmitErrors={this.clearSubmitErrors}
                                    component={InputGroup}
                                    type="email"
                                    label={copyFor(
                                        'formFields',
                                        'emailAddress',
                                        'email',
                                        capitalization.eachWord
                                    )}
                                    placeholder={copyFor(
                                        'placeholders',
                                        'emailAddress',
                                        'email'
                                    )}
                                />
                            </DynamicEmailRow>
                            {this.props.requiredFormElements
                                .filter(
                                    a => a.name !== 'NamePrefix' && a.name !== 'Topic'
                                )
                                .sort((a, b) => a.index - b.index)
                                .map((a, index) => {
                                    if (a.type === 'Address') {
                                        return (
                                            <DynamicEmailRow
                                                key={a.index}
                                                onBlur={() =>
                                                    this.checkForBlurError('address')
                                                }
                                            >
                                                <ReactGoogleAutocomplete
                                                    submissionError={this.state.errors.includes(
                                                        'address'
                                                    )}
                                                    clearSubmitErrors={
                                                        this.clearSubmitErrors
                                                    }
                                                    onPlaceSelectedCallback={address =>
                                                        this.props.loadLocalRepSecondaryAddressCheck(
                                                            address,
                                                            this.props
                                                                .representativeSearch,
                                                            this.props
                                                                .maximumRepresentativeSearch, //eslint-disable-line
                                                            this.props.presetRepData
                                                        )
                                                    }
                                                    formName={[
                                                        'emailDetailsFormMultiRepStep2',
                                                        'google_autocomplete'
                                                    ]}
                                                    fields={[
                                                        'address_components',
                                                        'formatted_address'
                                                    ]}
                                                    types={['address']}
                                                    componentRestrictions={{
                                                        country: ['US', 'UK', 'GB']
                                                    }}
                                                    setValidState={this.setValidState.bind(
                                                        this
                                                    )}
                                                />
                                            </DynamicEmailRow>
                                        );
                                    } else {
                                        return (
                                            <HalfRow
                                                key={a.index}
                                                allVisible={
                                                    this.props.requiredFormElements
                                                        .length === 7
                                                }
                                            >
                                                <Field
                                                    className={'fs-block'}
                                                    onBlur={() =>
                                                        this.checkForBlurError(
                                                            a.name.toLowerCase()
                                                        )
                                                    }
                                                    submissionError={this.state.errors.includes(
                                                        a.name.toLowerCase()
                                                    )}
                                                    clearSubmitErrors={
                                                        this.clearSubmitErrors
                                                    }
                                                    name={a.name.toLowerCase()}
                                                    key={index}
                                                    component={
                                                        a.options.length > 0
                                                            ? DropDownSelect
                                                            : InputGroup
                                                    }
                                                    options={a.options}
                                                    defaultOption={a.defaultOption}
                                                    type="text"
                                                    label={a.placeholder}
                                                    placeholder={a.placeholder}
                                                />
                                            </HalfRow>
                                        );
                                    }
                                })}
                        </Row>
                        <LegalInfoWrapper>
                            <FormGroupWrapper>
                                <GetUpdatesCheckbox
                                    style={{
                                        paddingBottom:
                                            this.props.endUserLocation.isEU &&
                                            !isPortrait()
                                                ? 0
                                                : 12,
                                        marginBottom:
                                            this.props.isRecordVideoAction &&
                                            !this.props.endUserLocation.isEU
                                                ? -12
                                                : 0
                                    }}
                                    isEU={this.props.endUserLocation.isEU}
                                    defaultChecked={!this.props.endUserLocation.isEU}
                                    labelText={this.props.getUpdatesText}
                                />
                                <LegalInfo align="mobile-right" />
                            </FormGroupWrapper>
                        </LegalInfoWrapper>
                        <ButtonGroup isSubmitting={this.props.isSubmitting}>
                            <TwoGroup>
                                <Button
                                    label={copyFor(
                                        'buttons',
                                        'back',
                                        'email',
                                        capitalization.eachWord
                                    )}
                                    onClick={this.props.onBackFromStep2}
                                    className="middleButton"
                                />
                                <OnSubmitValidator
                                    onClick={e => {
                                        e.preventDefault();
                                        const errors = submissionErrorHandler(
                                            this.props.currentFormValues,
                                            validateStep2,
                                            {
                                                isRecordVideoAction: this.props
                                                    .isRecordVideoAction
                                            }
                                        );
                                        if (
                                            errors.length === 0 &&
                                            !this.props.invalid &&
                                            this.state.valid
                                        ) {
                                            this.props.onSubmit();
                                        } else {
                                            this.setSubmitErrors(errors);
                                        }
                                    }}
                                >
                                    <Button
                                        primary={true}
                                        label={copyFor(
                                            'buttons',
                                            'send',
                                            'email',
                                            capitalization.eachWord
                                        )}
                                        type="submit"
                                        className="submitButton"
                                        disabled={
                                            this.props.invalid ||
                                            !this.state.valid ||
                                            this.props.isRepDoubleCheckLoading
                                        }
                                    />
                                </OnSubmitValidator>
                                <LoadingSpinner />
                            </TwoGroup>
                        </ButtonGroup>
                        {this.props.prefixModalVisible && (
                            <PrefixPopup
                                updatePrefix={val => {
                                    this.props.change('prefix', val);
                                    this.props.setPrefixModalVisibility(false);
                                }}
                            />
                        )}
                    </DetailsFormContainer>
                )}
            </ThemeConsumer>
        );
    }
}

DetailsFormMultiRepStep2.propTypes = {
    actionIdentifier: PropTypes.string,
    cacheFormData: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    currentFormValues: PropTypes.object,
    endUserLocation: PropTypes.object.isRequired,
    getUpdatesText: PropTypes.string,
    includesPrefix: PropTypes.bool.isRequired,
    includesTopic: PropTypes.bool,
    initialValues: PropTypes.object,
    invalid: PropTypes.bool.isRequired,
    isRecordVideoAction: PropTypes.bool,
    isRepDoubleCheckLoading: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    loadLocalRepSecondaryAddressCheck: PropTypes.func.isRequired,
    maximumRepresentativeSearch: PropTypes.shape({
        levels: PropTypes.array.isRequired,
        roles: PropTypes.array.isRequired
    }),
    onBackFromStep2: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    prefixModalVisible: PropTypes.bool,
    presetRepData: PropTypes.arrayOf(PropTypes.object),
    representativeSearch: PropTypes.shape({
        id: PropTypes.number.isRequired,
        levels: PropTypes.array.isRequired,
        roles: PropTypes.array.isRequired
    }),
    requiredFormElements: PropTypes.array.isRequired,
    setPrefixModalVisibility: PropTypes.func.isRequired,
    subject: PropTypes.string
};

export default compose(
    connect(
        state => {
            const formElements = requiredFormElements(state);
            const formElementsNames = formElements.map(e => e.name);
            const initialValues = {
                fullName: getCachedFullName(state),
                email: state.cachedFormData.email || '',
                getUpdates: !(state.widget.endUserLocation || {}).isEU
            };

            if (formElementsNames.includes('NamePrefix')) {
                initialValues.prefix = state.cachedFormData.prefix || '';
            }
            if (formElementsNames.includes('Phone')) {
                initialValues.phone = state.cachedFormData.phone || '';
            }

            const isRecordVideoAction = state.widget.activeAction.type === 'recordvideo';

            if (isRecordVideoAction) {
                initialValues.phone = state.cachedFormData.phone || '';

                const actionIdentifier = state.widget.activeAction.identifier;

                const cachedTopic = findCachedValueByActionIdentifier(
                    state.cachedFormData.topic,
                    actionIdentifier
                );

                const topic =
                    state.cachedFormData.topic.length > 0 && !!cachedTopic
                        ? cachedTopic
                        : formElements
                              .filter(a => a.name === 'Topic')
                              .map(a =>
                                  getActiveTopic(
                                      a.options,
                                      state.widget.activeAction.issue,
                                      state.widget.activeAction.subIssue
                                  )
                              )[0];

                const subject = state.widget.activeAction.subject || '';

                if (!!topic) {
                    initialValues.topic = topic;
                } else if (formElements.find(a => a.name === 'Topic')) {
                    initialValues.topic = ''; // placeholder so validation knows this is required
                }
                if (!topic && !!subject) {
                    initialValues.subject = subject;
                }
            }

            return {
                initialValues,
                requiredFormElements: formElements,
                isRepDoubleCheckLoading: state.localRep.isRepDoubleCheckLoading,
                prefixModalVisible: state.email.prefixModalVisible,
                presetRepData: state.localRep.localRepData.representative || [{}],
                representativeSearch: state.widget.activeAction.representativeSearch,
                maximumRepresentativeSearch: state.widget.maximumRepresentativeSearch,
                includesPrefix: !!formElements.find(a => a.name === 'NamePrefix'),
                localRepFullName:
                    state.localRep.localRepData.representative !== undefined
                        ? (state.localRep.localRepData.representative[0] || {}).fullName
                        : '',
                getUpdatesText: state.widget.activeAction.subscriptionOptInText,
                endUserLocation: state.widget.endUserLocation || {},
                currentFormValues: (state.form['emailDetailsFormMultiRepStep2'] || {})
                    .values,
                isRecordVideoAction
            };
        },
        {
            loadLocalRepSecondaryAddressCheck,
            cacheFormData
        }
    ),
    reduxForm({
        form: 'emailDetailsFormMultiRepStep2',
        validate: validateStep2,
        enableReinitialize: false,
        destroyOnUnmount: true
    })
)(DetailsFormMultiRepStep2);
